import {
  type FunctionComponent,
  type KeyboardEvent,
  useRef,
  useState,
} from 'react';

import { useNavigate } from 'react-router';

import { Button, Input, ThemeProvider } from '@dr.pogodin/react-utils';
import { captureException } from '@sentry/react';

import { PasswordResetChecks } from 'components';

import Throbber from 'components/Throbber';
import { usePasswordResetPageQuery } from 'hooks';
import { resetPasswordEnd } from 'services';
import { evalPasswordStrength, useGlobalState } from 'utils';

import IconEye from 'assets/images/icon-eye-white-24px.svg';

import Logo from 'assets/images/logo-soul-sanctuary-new-110px.svg';

import throbberTheme from 'themes/AppThrobber/black.scss';

import inputTheme from 'themes/Input/admin/with-inner-label-60px.scss';

import eyeIconButtonTheme from './eye-icon-button.scss';
import passwordInputTheme from './password-input.scss';

import S from './style.scss';

function noop() {
  // NOOP
}

// TODO: It should have different modes, to create a new account,
// and to reset the password on an existing one.

const AdminPasswordResetPage: FunctionComponent = () => {
  const navigate = useNavigate();

  const [busy, setBusy] = useState(false);

  const [, setUser] = useGlobalState('user');

  const [passwordA, setPasswordA] = useState<string>('');
  const [passwordB, setPasswordB] = useState<string>('');

  const passwordInputRefB = useRef<HTMLInputElement>(null);

  const [showPasswordA, setShowPasswordA] = useState(false);
  const [showPasswordB, setShowPasswordB] = useState(false);

  const {
    email,
    firstName,
    lastName,
    token,
    userId,
  } = usePasswordResetPageQuery();

  const password = evalPasswordStrength(passwordA, [
    ...email.split('@'),
    firstName,
    lastName,
  ]);

  const ready: boolean = password.strength > 2 && passwordA === passwordB;

  const submit = async () => {
    try {
      setBusy(true);
      const user = await resetPasswordEnd({
        email,
        password: passwordA,
        token,
        userId,
      });
      setUser(user);
      void navigate('/');
    } catch (e) {
      captureException(e);
      // eslint-disable-next-line no-alert
      alert('Failed to set password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={S.container}>
      <form autoComplete="on" styleName="inputBlockOuterContainer">
        <Logo styleName="logo" />
        <h1>Set a password</h1>
        <p>To finish creating your account please set a password</p>
        <input
          // This hidden input is necessary for the optimal auto-complete and
          // credential store support.
          autoComplete="username"
          id="username"
          name="username"
          onChange={noop}
          styleName="hidden"
          value={email}
        />
        <ThemeProvider themes={{ Input: inputTheme }}>
          <div styleName="passwordInputBlock">
            <Input
              autoComplete="new-password"
              id="passwordA"
              label="Password"
              name="passwordA"
              onChange={(e) => {
                setPasswordA(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') passwordInputRefB.current?.focus();
              }}
              theme={passwordInputTheme}
              type={showPasswordA ? 'text' : 'password'}
              value={passwordA}
            />
            <Button
              onClick={() => {
                setShowPasswordA(!showPasswordA);
              }}
              onKeyDown={(e: KeyboardEvent) => {
                if (e.key === 'Enter') {
                  setShowPasswordA(!showPasswordA);
                }
              }}
              theme={eyeIconButtonTheme}
            >
              <IconEye />
            </Button>
          </div>
          <div styleName="passwordInputBlock">
            <Input
              autoComplete="new-password"
              id="passwordB"
              label="Repeat Password"
              name="passwordB"
              onChange={(e) => {
                setPasswordB(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (ready) void submit();
                }
              }}
              ref={passwordInputRefB}
              theme={passwordInputTheme}
              type={showPasswordB ? 'text' : 'password'}
              value={passwordB}
            />
            <Button
              onClick={() => {
                setShowPasswordB(!showPasswordB);
              }}
              onKeyDown={(e: KeyboardEvent) => {
                if (e.key === 'Enter') {
                  setShowPasswordB(!showPasswordB);
                }
              }}
              theme={eyeIconButtonTheme}
            >
              <IconEye />
            </Button>
          </div>
        </ThemeProvider>
        <PasswordResetChecks
          passwordEvalRes={password}
          passwordsMatch={passwordA ? passwordA === passwordB : undefined}
        />
        {busy ? (
          <div styleName="throbberContainer">
            <Throbber theme={throbberTheme} />
          </div>
        ) : (
          <Button
            disabled={!ready}
            onClick={() => {
              void submit();
            }}
          >
            Create Account
          </Button>
        )}
      </form>
      <div styleName="placeholder" />
    </div>
  );
};

export default AdminPasswordResetPage;
